import type { Agent } from "../types/integration";

/**
 * Theo's agent roster for navigation, derived from the live agents the API returns.
 *
 * Every agent has its own monitor: the default agent at `/theo-automations/monitor`, and each
 * other agent at `/agents/<slug>/monitor`. Configure is the same screen for every agent. The
 * default agent's URL stays `/theo-automations/configure`.
 *
 * Ids here are agent slugs, because a slug is an agent's immutable identity; its name is only a
 * label and an MSP may rename it freely.
 */

export interface TheoAgentNav {
  id: string;
  /** Sidebar label (the agent's current name). */
  label: string;
  /** Topbar and placeholder heading. */
  title: string;
  /** One line on the placeholder page. */
  summary: string;
  monitorTo: string;
  configureTo: string;
  /** This agent uses `/agents/<slug>/...` instead of the default Auto Mode routes. */
  placeholder: boolean;
}

/** Matches agent.slug for the built-in default agent seeded for every MSP. */
export const DEFAULT_AGENT_SLUG = "theo-technician";

/**
 * Nav entry for one live agent.
 *
 * Routes key on `slug`, never on `name`: the slug is the agent's immutable identity, so a rename
 * does not change its URLs or break a bookmark. The default agent keeps the Auto Mode URLs.
 * Every agent uses the same configure screen.
 */
export function agentNavFromAgent(agent: Agent): TheoAgentNav {
  const isDefault = agent.is_default;
  const summary = agent.description?.trim()
    ? agent.description
    : isDefault
      ? "Works tickets Theo is allowed to take from the queue."
      : agentTriggerSummary(agent);
  return {
    id: agent.slug,
    label: agent.name,
    title: agent.name,
    summary,
    monitorTo: isDefault ? "/theo-automations/monitor" : `/agents/${agent.slug}/monitor`,
    configureTo: isDefault ? "/theo-automations/configure" : `/agents/${agent.slug}/configure`,
    placeholder: !isDefault,
  };
}

/** A one-line description of when an agent picks up a ticket, for a roster row. */
export function agentTriggerSummary(agent: Agent): string {
  const trigger = agent.trigger;
  if (!agent.enabled) return "Disabled — it will not pick up tickets.";
  const topics = trigger?.ticket_types ?? [];
  if (topics.length > 0) return `Picks up: ${topics.join(", ")}.`;
  const queues = trigger?.queues ?? [];
  if (queues.length > 0) return `Picks up tickets in queue ${queues.join(", ")}.`;
  const categories = (trigger?.rules ?? []).flatMap((rule) =>
    rule.theo_categories.map((category) => (category.l2 ? `${category.l1} > ${category.l2}` : category.l1))
  );
  if (categories.length > 0) return `Picks up: ${categories.join(", ")}.`;
  return "No trigger criteria yet, so it will not be selected for a ticket.";
}

/**
 * The roster, default agent first, then the rest by name.
 *
 * Matches the server's own ordering so the sidebar does not reshuffle when the API answers.
 */
export function agentNavRoster(agents: Agent[]): TheoAgentNav[] {
  return [...agents]
    .sort((left, right) => {
      if (left.is_default !== right.is_default) return left.is_default ? -1 : 1;
      return left.name.localeCompare(right.name);
    })
    .map(agentNavFromAgent);
}

/**
 * Shown before GET /agents has answered, and if it fails.
 *
 * Every MSP has the default agent, so showing it immediately keeps the sidebar from flickering
 * empty on load. It is a placeholder for a real row, not a mock of agents that do not exist.
 */
export const FALLBACK_AGENT_ROSTER: TheoAgentNav[] = [
  {
    id: DEFAULT_AGENT_SLUG,
    label: "Theo Agent",
    title: "Theo Agent",
    summary: "Works tickets Theo is allowed to take from the queue.",
    monitorTo: "/theo-automations/monitor",
    configureTo: "/theo-automations/configure",
    placeholder: false,
  },
];

export function findTheoAgent(id: string | undefined, roster: TheoAgentNav[] = FALLBACK_AGENT_ROSTER): TheoAgentNav | undefined {
  return roster.find((agent) => agent.id === id);
}
